import { t } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import { useProfile } from "@/hooks/use-profile";
import { usePreferences } from "@/hooks/use-preferences";
import { useToast } from "@/components/ui/toast";
import { changePassword } from "@/lib/api/user";
import { AvatarUpload } from "@/components/settings/avatar-upload";
import { ProfileForm } from "@/components/settings/profile-form";
import { NotificationPreferencesCard } from "@/components/settings/notification-preferences";
import { LanguageSwitcher } from "@/components/settings/language-switcher";
import { PasswordChangeForm } from "@/components/settings/password-change-form";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { NotificationPreferences } from "@/types/user";
import type { PasswordChangeInput } from "@/types/user";

export default function SettingsPage() {
  const { locale } = useAppContext();
  const { toast } = useToast();
  const {
    profile,
    loading: profileLoading,
    saving: profileSaving,
    uploadAvatar,
    refetch,
  } = useProfile();
  const {
    preferences,
    loading: prefsLoading,
    saving: prefsSaving,
    updatePreferences,
  } = usePreferences();

  async function handleUploadAvatar(file: File) {
    const success = await uploadAvatar(file);
    if (success) {
      toast(t(locale, "avatarUploaded"), "success");
    } else {
      toast(t(locale, "errorLoadingData"), "error");
    }
  }

  async function handleToggleNotification(
    key: keyof NotificationPreferences,
    value: boolean
  ) {
    await updatePreferences({ [key]: value });
  }

  async function handleChangePassword(
    data: PasswordChangeInput
  ): Promise<boolean> {
    try {
      const response = await changePassword(data);
      if (response.success) {
        toast(t(locale, "passwordChanged"), "success");
        return true;
      }
      toast(response.message ?? t(locale, "errorLoadingData"), "error");
      return false;
    } catch {
      toast(t(locale, "errorLoadingData"), "error");
      return false;
    }
  }

  if (profileLoading || prefsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" label={t(locale, "loadingData")} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto w-full grid grid-cols-1 xl:grid-cols-12 gap-6 md:gap-8">
      <div className="xl:col-span-4 space-y-6">
        <NotificationPreferencesCard
          preferences={preferences}
          onToggle={handleToggleNotification}
          loading={prefsSaving}
        />
        <LanguageSwitcher />
        <PasswordChangeForm onSubmit={handleChangePassword} loading={false} />
      </div>

      <div className="xl:col-span-8">
        <section className="bg-card p-6 md:p-8 rounded-lg shadow-sm border border-border h-full flex flex-col">
          <div className="flex flex-col items-center mb-10">
            <AvatarUpload
              avatarUrl={profile?.avatar_url ?? null}
              onUpload={handleUploadAvatar}
              loading={profileSaving}
            />
          </div>
          <ProfileForm
            profile={profile}
            onSuccess={refetch}
            loading={profileSaving}
          />
        </section>
      </div>
    </div>
  );
}
